import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeTrip, setActiveTrip] = useState(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const loadTrip = async () => {
      if (!user?.id) return;
      const { data: trip } = await tripService?.getActiveTrip(user?.id);
      setActiveTrip(trip);
    };
    loadTrip();
  }, [user]);

  const currentTrip = {
    name: 'Caribbean Coastal Cruise',
    daysRemaining: 12,
    startDate: '2026-01-10',
    endDate: '2026-01-25'
  };

  // Generate user initials from full name or email
  const getUserInitials = () => {
    if (userProfile?.full_name) {
      const nameParts = userProfile?.full_name?.trim()?.split(' ');
      if (nameParts?.length >= 2) {
        return `${nameParts?.[0]?.[0]}${nameParts?.[nameParts?.length - 1]?.[0]}`?.toUpperCase();
      }
      return nameParts?.[0]?.substring(0, 2)?.toUpperCase();
    }
    if (user?.email) {
      return user?.email?.substring(0, 2)?.toUpperCase();
    }
    return 'U';
  };

  const navigationItems = [
    {
      label: 'Trip Settings',
      path: '/trip-settings',
      icon: 'Settings',
      tooltip: 'Configure trip settings'
    },
    {
      label: 'Inventory',
      path: '/inventory-management',
      icon: 'Package',
      tooltip: 'Manage your provisions'
    },
    {
      label: 'Meal Planning',
      path: '/meal-planning',
      icon: 'Calendar',
      tooltip: 'Plan meals for your trip'
    }
  ];

  const userMenuItems = [
    {
      label: 'Account Preferences',
      icon: 'User',
      action: () => {
        navigate('/account-settings');
        setIsUserMenuOpen(false);
      }
    },
    {
      label: 'Help & Support',
      icon: 'HelpCircle',
      action: () => {
        console.log('Navigate to help');
        setIsUserMenuOpen(false);
      }
    },
    {
      label: 'Logout',
      icon: 'LogOut',
      isDanger: true,
      action: () => {
        navigate('/login');
        setIsUserMenuOpen(false);
      }
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const isAuthPage = location?.pathname === '/login' || location?.pathname === '/register';

  if (isAuthPage) {
    return null;
  }

  return (
    <header className="header-container">
      <div className="header-content">
        <Link to="/meal-planning" className="header-logo">
          <div className="logo-icon">
            <img src="/assets/images/B42A68D7-7405-4964-872F-D36C4B1CBA1C-1768359280145.jpg" alt="GalleyPlan Logo" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
          </div>
          <span className="logo-text">GalleyPlan</span>
        </Link>

        <nav className="nav-primary">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`nav-link ${location?.pathname === item?.path ? 'active' : ''}`}
              title={item?.tooltip}
            >
              <Icon name={item?.icon} size={20} />
              <span>{item?.label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-accent/10 transition-colors"
            aria-label="Toggle theme"
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Icon name={isDarkMode ? "Sun" : "Moon"} size={20} color="var(--color-foreground)" />
          </button>

          <div className="relative" ref={userMenuRef}>
            <button
              className="user-menu-trigger"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              aria-label="User menu"
              aria-expanded={isUserMenuOpen}
            >
              <div className="user-avatar">{getUserInitials()}</div>
              <Icon 
                name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"} 
                size={16} 
              />
            </button>

            {isUserMenuOpen && (
              <div className="user-menu-dropdown">
                <div className="user-menu-header">
                  <div className="user-menu-name">{userProfile?.full_name || user?.email || 'User'}</div>
                  <div className="user-menu-email">{user?.email || ''}</div>
                </div>
                <div className="user-menu-items">
                  {userMenuItems?.map((item, index) => (
                    <button
                      key={index}
                      className={`user-menu-item ${item?.isDanger ? 'danger' : ''}`}
                      onClick={item?.action}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;